import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css"; // Default styles for the calendar
import { useEffect, useMemo, useState } from "react";
import { GrPrevious, GrNext } from "react-icons/gr";
import { Button, Col, Empty, Row, Skeleton } from "antd";

import moment from "moment";
import { useTranslation } from "react-i18next";

interface Props {
  slots: any;
  selectedSlot: any;
  setSelectedSlot: (slot: any) => void;
  /** When false, slot list shows guidance instead of time buttons */
  hasSelectedBranch?: boolean;
}

const OrderCalendar = ({
  slots,
  selectedSlot,
  setSelectedSlot,
  hasSelectedBranch = true,
}: Props) => {
  const [date, setDate] = useState<any>(new Date());
  const [displayableSlots, setDisplayableSlots] = useState<any[]>([]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setDisplayableSlots(
      slots?.list?.filter(
        (el: any) =>
          moment(el.start_date).format("YYYY/MM/DD") ===
          moment(date).format("YYYY/MM/DD")
      ) ?? []
    );
  }, [slots, date]);

  const dateLabel = useMemo(
    () =>
      moment(date)
        .locale(i18n.language === "ar" ? "ar" : "en")
        .format("dddd, D MMMM YYYY"),
    [date, i18n.language]
  );

  const availableCount = useMemo(
    () => displayableSlots.filter((el: any) => el.is_available).length,
    [displayableSlots]
  );

  const toSelectSlot = (slot: any) => {
    setSelectedSlot(slot);
  };
  useEffect(() => {
    const abbrElements = document.querySelectorAll(
      ".ac-order-calendar .react-calendar abbr[title]"
    );
    abbrElements.forEach((abbr) => abbr.removeAttribute("title"));
  }, [date]);

  const slotsIdle = slots?.status === "idle";

  const showNoSlotsEmpty =
    hasSelectedBranch &&
    !slotsIdle &&
    displayableSlots.length === 0 &&
    slots?.status === "succedded";

  const showBranchHint = !hasSelectedBranch && !slotsIdle;

  const pickedTimeStr = selectedSlot
    ? moment(selectedSlot.start_date)
        .locale(i18n.language === "ar" ? "ar" : "en")
        .format("h:mm A")
    : "";

  return (
    <div
      id="order-appointment-calendar"
      className="ac-order-calendar ac-order-panel ac-order-panel--calendar ac-order-panel--accent"
    >
      <header className="ac-order-calendar__head">
        <div className="ac-order-panel__title-row ac-order-calendar__title-block">
          <span className="ac-order-panel__step-num" aria-hidden>
            2
          </span>
          <div className="ac-order-calendar__titles">
            <h2 className="ac-order-calendar__title ac--font-expo">
              {t("profile.order.sectionSchedule")}
            </h2>
            <p className="ac-order-calendar__subtitle">
              {t("profile.order.timesForDay", { date: dateLabel })}
            </p>
            {hasSelectedBranch && availableCount > 0 && (
              <p className="ac-order-calendar__pill" role="status">
                {t("profile.order.slotsOnDay", { count: availableCount })}
              </p>
            )}
            {hasSelectedBranch ? (
              <p className="ac-order-calendar__help">
                {t("profile.order.timeSlotHelp")}
              </p>
            ) : null}
            {selectedSlot ? (
              <p
                className="ac-order-calendar__picked"
                role="status"
                aria-live="polite"
              >
                {t("profile.order.slotPickedBanner", { time: pickedTimeStr })}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <div className="ac-order-calendar__grid">
        <div className="ac-order-calendar__picker-wrap">
          <div className="ac-order-calendar__picker-shell">
            <p className="ac-order-calendar__picker-caption">
              {t("profile.order.calendarCaption")}
            </p>
            <Calendar
            className="ac-order-calendar__picker"
            value={date}
            minDate={new Date()}
            calendarType="hebrew"
            onChange={(e) => setDate(e)}
            prevLabel={
              <span className="ac-order-calendar__nav-icon">
                {i18n.language === "ar" ? <GrNext /> : <GrPrevious />}
              </span>
            }
            nextLabel={
              <span className="ac-order-calendar__nav-icon">
                {i18n.language === "ar" ? <GrPrevious /> : <GrNext />}
              </span>
            }
            prev2Label={null}
            next2Label={null}
            locale={i18n.language}
          />
          </div>
        </div>

        <div className="ac-order-calendar__slots-column">
          <Row gutter={[16, 16]} className="ac-order-calendar__slots-row">
            {slotsIdle && (
              <Col span={24}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Col>
            )}

            {showBranchHint && (
              <Col span={24}>
                <div className="ac-order-calendar__hint" role="status">
                  {t("profile.order.slotsNeedBranch")}
                </div>
              </Col>
            )}

            {showNoSlotsEmpty && (
              <Col span={24}>
                <div className="ac-order-calendar__empty">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="ac-order-calendar__empty-desc">
                        <span className="ac-order-calendar__empty-text ac--font-expo">
                          {t("profile.order.message")}
                        </span>
                        <span className="ac-order-calendar__empty-sub">
                          {t("profile.order.noSlotsTryOtherDay")}
                        </span>
                      </div>
                    }
                  />
                </div>
              </Col>
            )}

            {hasSelectedBranch &&
              displayableSlots.map((el: any) => {
                const timeStr = moment(el.start_date)
                  .locale(i18n.language === "ar" ? "ar" : "en")
                  .format("h:mm A");
                return (
                  <Col
                    xxl={8}
                    xl={8}
                    md={8}
                    lg={8}
                    sm={12}
                    xs={12}
                    key={el.id}
                    className="ac-order-calendar__slot-col"
                  >
                    {el.is_available ? (
                      <Button
                        htmlType="button"
                        onClick={() => toSelectSlot(el)}
                        className={`ac--order-slot-btn ac--order-slot-btn-available ${
                          selectedSlot?.id === el.id
                            ? "ac--order-slot-btn-active"
                            : ""
                        }`}
                        disabled={moment(el.start_date) < moment()}
                        aria-label={t("profile.order.bookSlotAria", {
                          time: timeStr,
                        })}
                        aria-pressed={selectedSlot?.id === el.id}
                      >
                        {timeStr}
                      </Button>
                    ) : (
                      <Button
                        htmlType="button"
                        className="ac--order-slot-btn ac--order-slot-btn-unavailable"
                        disabled
                        aria-label={timeStr}
                      >
                        {timeStr}
                      </Button>
                    )}
                  </Col>
                );
              })}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default OrderCalendar;
